import {
  COPY_FIELD_NAMES,
  copyFieldSchema,
  GROUP_EDIT_ACTIONS,
  groupEditActionSchema,
  type AiCopyGroupEditRequest,
  type AiCopyGroupEditResponse,
} from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  fill,
  NO_ERRORS,
  SelectField,
  TextAreaField,
  TextField,
  type FieldErrors,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';
import {
  keys,
  platformApi,
  tenantCopyGroupsQuery,
  tenantManufacturersQuery,
} from '../api/platform';
import { he } from '../i18n/he';
import { formatCount } from '../lib/ai';
import { errorMessage } from '../lib/format';
import { mergeErrors } from '../lib/forms';
import {
  categoryOptions,
  EMPTY_GROUP_EDIT,
  groupEditReady,
  groupEditRequest,
  groupEditText,
  type GroupEditDraft,
} from '../lib/group-edit';

const words = he.groupEdit;

/** The answer of a group edit, and whether it was only a preview. */
interface Outcome {
  preview: boolean;
  result: AiCopyGroupEditResponse;
}

/**
 * Edits one field of the copy of a whole group of the store's products (adr/0043): a category
 * with everything below it, a manufacturer, or both. A preview counts the products and shows a
 * few of them before and after; only a preview of exactly this edit can be saved, after a
 * confirmation. Nothing is written by the engine.
 */
export function CopyGroupEdit({ tenantId, disabled }: { tenantId: string; disabled: boolean }) {
  const queryClient = useQueryClient();
  // Every category of the store is listed under any state of the copy; READY is just one.
  const categories = useQuery(tenantCopyGroupsQuery(tenantId, 'READY'));
  const manufacturers = useQuery(tenantManufacturersQuery(tenantId));
  const [draft, setDraft] = useState<GroupEditDraft>(EMPTY_GROUP_EDIT);
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  /** The shown preview is of the edit as it is now (any change of the form clears it). */
  const [previewed, setPreviewed] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const send = useMutation({
    mutationFn: (body: AiCopyGroupEditRequest) => platformApi.groupEditCopy(tenantId, body),
    onSuccess: async (result, body) => {
      const preview = body.preview === true;
      setConfirming(false);
      setOutcome({ preview, result });
      setPreviewed(preview);
      if (!preview) {
        await Promise.all(
          [keys.tenantCopy(tenantId), keys.tenantAi(tenantId), keys.audit(tenantId)].map(
            (queryKey) => queryClient.invalidateQueries({ queryKey }),
          ),
        );
      }
    },
    onError: () => {
      setConfirming(false);
    },
  });
  const errors = mergeErrors(localErrors, send.error);

  const change = (next: Partial<GroupEditDraft>) => {
    setDraft((current) => ({ ...current, ...next }));
    setLocalErrors(NO_ERRORS);
    setOutcome(null);
    setPreviewed(false);
    send.reset();
  };
  const request = (preview: boolean) => {
    const parsed = groupEditRequest(draft, preview);
    if (!parsed.ok) {
      setLocalErrors(parsed.errors);
      return null;
    }
    setLocalErrors(NO_ERRORS);
    return parsed.value;
  };
  const onPreview = (event: SyntheticEvent) => {
    event.preventDefault();
    const body = request(true);
    if (body !== null) send.mutate(body);
  };
  // Only a preview of exactly this edit is saved, and only when it changes something.
  const canSave = previewed && outcome !== null && outcome.result.changed > 0 && !disabled;

  return (
    <Card title={words.title}>
      <form
        onSubmit={onPreview}
        noValidate
        className="flex flex-col gap-4"
        data-testid="group-edit"
        aria-label={words.title}
      >
        <p className="text-sm text-slate-600">{words.intro}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label={words.category}
            name="categoryId"
            data-testid="group-edit-category"
            value={draft.categoryId}
            error={errors['group.categoryId']}
            onChange={(event) => {
              change({ categoryId: event.target.value });
            }}
          >
            <option value="">{words.anyCategory}</option>
            {categoryOptions(categories.data?.items ?? []).map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={words.manufacturer}
            name="manufacturer"
            data-testid="group-edit-manufacturer"
            value={draft.manufacturer}
            error={errors['group.manufacturer']}
            onChange={(event) => {
              change({ manufacturer: event.target.value });
            }}
          >
            <option value="">{words.anyManufacturer}</option>
            {(manufacturers.data?.items ?? []).map((item) => (
              <option key={item.name} value={item.name}>
                {`${item.name} (${formatCount(item.productCount)})`}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={words.field}
            name="field"
            data-testid="group-edit-field"
            value={draft.field}
            onChange={(event) => {
              change({ field: copyFieldSchema.parse(event.target.value) });
            }}
          >
            {COPY_FIELD_NAMES.map((field) => (
              <option key={field} value={field}>
                {he.aiCopy.fields[field]}
              </option>
            ))}
          </SelectField>
          <SelectField
            label={words.action}
            name="action"
            data-testid="group-edit-action"
            value={draft.action}
            onChange={(event) => {
              change({ action: groupEditActionSchema.parse(event.target.value) });
            }}
          >
            {GROUP_EDIT_ACTIONS.map((action) => (
              <option key={action} value={action}>
                {words.actions[action]}
              </option>
            ))}
          </SelectField>
        </div>
        {errors['group'] === undefined ? null : (
          <p role="alert" className="text-xs text-red-600" data-testid="group-edit-need-group">
            {errors['group']}
          </p>
        )}
        {draft.action === 'REPLACE' ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextAreaField
              label={words.find}
              name="find"
              rows={2}
              maxLength={500}
              hint={words.findHint}
              data-testid="group-edit-find"
              value={draft.find}
              error={errors['edit.find']}
              onChange={(event) => {
                change({ find: event.target.value });
              }}
            />
            <TextAreaField
              label={words.replace}
              name="replace"
              rows={2}
              maxLength={2_000}
              hint={words.replaceHint}
              data-testid="group-edit-replace"
              value={draft.replace}
              error={errors['edit.replace']}
              onChange={(event) => {
                change({ replace: event.target.value });
              }}
            />
          </div>
        ) : (
          <TextField
            label={words.text}
            name="text"
            maxLength={2_000}
            hint={words.textHint}
            data-testid="group-edit-text"
            value={draft.text}
            error={errors['edit.text']}
            onChange={(event) => {
              change({ text: event.target.value });
            }}
          />
        )}

        {send.isError ? (
          <Alert>{errorMessage(send.error, { CONFLICT: words.changed })}</Alert>
        ) : null}
        {outcome === null ? null : (
          <div
            className="flex flex-col gap-3"
            data-testid="group-edit-result"
            data-preview={outcome.preview}
          >
            <Alert tone={outcome.preview ? 'info' : 'success'}>
              {groupEditText(outcome.result, outcome.preview)}
            </Alert>
            {outcome.preview && outcome.result.examples.length > 0 ? (
              <Examples result={outcome.result} />
            ) : null}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            tone="secondary"
            data-testid="group-edit-preview"
            busy={send.isPending && send.variables.preview === true}
            disabled={disabled || !groupEditReady(draft)}
          >
            {words.previewButton}
          </Button>
          <Button
            data-testid="group-edit-apply"
            disabled={!canSave || send.isPending}
            onClick={() => {
              setConfirming(true);
            }}
          >
            {words.apply}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirming}
        title={words.confirmTitle}
        confirmLabel={words.apply}
        confirmTestId="group-edit-confirm"
        busy={send.isPending}
        onConfirm={() => {
          const body = request(false);
          if (body !== null) send.mutate(body);
        }}
        onClose={() => {
          setConfirming(false);
        }}
      >
        {fill(words.confirmBody, { count: formatCount(outcome?.result.changed ?? 0) })}
      </ConfirmDialog>
    </Card>
  );
}

/** A few products of the preview, before and after. */
function Examples({ result }: { result: AiCopyGroupEditResponse }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-slate-500">{words.examples}</p>
      <ul className="flex flex-col gap-2" data-testid="group-edit-examples">
        {result.examples.map((example) => (
          <li key={example.productId} className="rounded-md p-3 text-sm ring-1 ring-slate-200">
            <p className="font-semibold break-words text-slate-900">{example.name}</p>
            <dl className="mt-1 flex flex-col gap-1">
              <div>
                <dt className="text-xs text-slate-500">{words.before}</dt>
                <dd className="max-h-40 overflow-y-auto break-words whitespace-pre-line text-slate-600">
                  {example.before}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-slate-500">{words.after}</dt>
                <dd className="max-h-40 overflow-y-auto break-words whitespace-pre-line text-slate-900">
                  {example.after}
                </dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}
